import React from "react";
import { useTranslation } from "react-i18next";
import { TWebsiteBuilderSectionType } from "../../../../nodes/section/websiteBuilderSection";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TWebsiteBuilderSectionViewPosition } from "./WebsiteBuilderSectionView";
import { useAppDispatch } from "hooks/redux";
import { websiteBuilderAddNode, websiteBuilderSelectNode } from "redux/actions/websiteBuilder/websiteBuilder";
import { generateDefaultContent } from "../../../../nodes/section/websiteBuilderContent";
import { TWebsiteBuilderTreeRefMap } from "../../../../hooks/treeRefMap/useTreeRefMapRegistration";
import { websiteBuilderZLayers } from "../../../../websiteBuilderZLayers";
import { TWebsiteBuilderScrollContainerRef } from "../../../../WebsiteBuilderEditor";

interface ISectionAddSectionButtonsProps {
  parentId: string;
  type: TWebsiteBuilderSectionType;
  position: TWebsiteBuilderSectionViewPosition;
  maxIndex: number;
  hovered: boolean;
  scrollContainerRef: TWebsiteBuilderScrollContainerRef;
  treeRefMap: TWebsiteBuilderTreeRefMap;
}

type TAddSectionDirection = "up" | "down";

export function SectionAddSectionButtons(props: ISectionAddSectionButtonsProps) {
  const { parentId, type, position, maxIndex, hovered, scrollContainerRef, treeRefMap } = props;
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  function handleAddSection(direction: TAddSectionDirection) {
    let index: number = null;

    if (position.type === "outer") {
      if (type === "header" && direction === "down") {
        index = 0;
      } else if (type === "footer" && direction === "up") {
        index = maxIndex + 1;
      }
    } else if (position.type === "inner") {
      index = position.index + (direction === "up" ? 0 : 1);
    }

    if (index == null) {
      return;
    }

    const prevScrollTop = scrollContainerRef.current?.scrollTop;

    const newSection = generateDefaultContent();

    dispatch(
      websiteBuilderAddNode({
        snapshot: {
          parentId,
          node: newSection,
          index,
        },
      }),
    );

    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          parentId,
          id: newSection.id,
          editingMode: "none",
        },
      }),
    );

    requestAnimationFrame(() => {
      const newSectionRef = treeRefMap.current.get(newSection.id);

      if (newSectionRef && scrollContainerRef.current && prevScrollTop != null) {
        const layoutShift = direction === "up" ? newSectionRef.getBoundingClientRect().height : 0;

        scrollContainerRef.current.scrollTo({ top: prevScrollTop + layoutShift });

        setTimeout(() => {
          newSectionRef.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      }
    });
  }

  return (
    <>
      {hovered && type !== "header" && (
        <button
          className="website-builder-section-add-section-top-button"
          style={{
            zIndex: websiteBuilderZLayers.designSpace.sectionEditorUI,
          }}
          onPointerDown={e => e.stopPropagation()}
          onClick={() => handleAddSection("up")}
        >
          <FontAwesomeIcon icon={faPlus} /> {t("Add Section")}
        </button>
      )}
      {hovered && type !== "footer" && (
        <button
          className="website-builder-section-add-section-bottom-button"
          style={{
            zIndex: websiteBuilderZLayers.designSpace.sectionEditorUI,
          }}
          onPointerDown={e => e.stopPropagation()}
          onClick={() => handleAddSection("down")}
        >
          <FontAwesomeIcon icon={faPlus} /> {t("Add Section")}
        </button>
      )}
    </>
  );
}
